import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiRequestError } from './api-client.js';
import { redirectToSignIn, SESSION_QUERY_KEY } from '../features/auth/session.js';

/**
 * One query client for the app.
 *
 * Retrying a 4xx is pointless and, on a 401, actively harmful: it multiplies
 * the failed requests before the refresh flow can run. Only 5xx and network
 * faults are retried.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleSessionExpiry }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleSessionExpiry(error);
      refreshContextOnClosedShift(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * ui-contract.md section 6 (SH-25): a session that ends mid-work answers any
 * request with UNAUTHENTICATED. Once this tab holds a session, that sends the
 * employee to sign in again and back to this page instead of showing the error
 * inside a form. Before a session exists (the launch screen, tablet pages) the
 * screen that made the request handles the 401 itself; other 401 codes are
 * business errors shown in place.
 */
function handleSessionExpiry(error: unknown): void {
  if (!(error instanceof ApiRequestError) || error.code !== 'UNAUTHENTICATED') return;
  if (queryClient.getQueryData(SESSION_QUERY_KEY) === undefined) return;
  redirectToSignIn();
}

/** The context query of this app; kept literal so this module imports no feature code. */
const CONTEXT_QUERY_KEY = ['pda', 'context'] as const;

/**
 * A save refused because no shift is open (KAN-38) means the cached context is
 * stale: read it again so the home shows the no-shift state at once.
 */
function refreshContextOnClosedShift(error: unknown): void {
  if (!(error instanceof ApiRequestError) || error.code !== 'REVOLVING_BALANCE_REQUIRED') return;
  void queryClient.invalidateQueries({ queryKey: CONTEXT_QUERY_KEY });
}
